import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { documentApi } from '../api';
import { queryKeys } from '../queries/keys';
import type { AnnotationInfo } from '../types';

/** `'always'` refetches each time the consumer mounts, and shows the cached
 *  list until the refetch answers. */
type RefetchOnMount = { refetchOnMount?: 'always' };

/** Every document the signed-in user can see. */
export function useDocuments({ refetchOnMount }: RefetchOnMount = {}) {
  return useQuery({
    queryKey: queryKeys.documents(),
    queryFn: () => documentApi.list(),
    refetchOnMount,
  });
}

/** The annotation index. `isAdmin` asks for every user's annotations, and is
 *  part of the key, so the admin and the per-user lists are cached apart. */
export function useAnnotations<SelectedAnnotations = AnnotationInfo[]>(
  isAdmin: boolean,
  { select, refetchOnMount }: RefetchOnMount & {
    select?: (list: AnnotationInfo[]) => SelectedAnnotations;
  } = {},
) {
  return useQuery({
    queryKey: queryKeys.annotations(isAdmin),
    queryFn: () => documentApi.listAnnotations(undefined, isAdmin),
    select,
    refetchOnMount,
  });
}

/** Assigns each drawing to one annotator. */
export function useAssignDocuments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentIds, assignee }: { documentIds: string[]; assignee: string }) =>
      Promise.all(documentIds.map((documentId) => documentApi.assignDocument(documentId, assignee))),
    // An assignment creates or updates the drawing's annotation row, which the
    // document list is read from too. Every annotation list refetches, the admin
    // one included, because the drawing page may hold it.
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.documents() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.allAnnotations() }),
    ]),
  });
}
